/**
 * ConcoursBf — Frontend API Client
 *
 * Single configuration point for the backend URL.
 * All services use this client for API calls.
 */
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
export const CONTENT_API_URL = `${API_URL}/content`;

const DEFAULT_TIMEOUT_MS = 20000;

/**
 * apiClient — wrapper around fetch with:
 * - optional JWT injection
 * - timeout protection
 * - safe JSON parsing
 * - normalized HTTP errors
 *
 * Custom options:
 * - auth: false skips Authorization for public endpoints
 * - timeoutMs: override the default request timeout
 */
async function apiClient(endpoint, options = {}) {
  const {
    auth = true,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    headers: providedHeaders = {},
    signal: providedSignal,
    ...fetchOptions
  } = options;

  const headers = {
    Accept: 'application/json',
    ...providedHeaders,
  };

  if (
    fetchOptions.body !== undefined &&
    fetchOptions.body !== null &&
    !Object.keys(headers).some((name) => name.toLowerCase() === 'content-type')
  ) {
    headers['Content-Type'] = 'application/json';
  }

  if (auth) {
    const token = localStorage.getItem('concours_bf_token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const controller = providedSignal ? null : new AbortController();
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
      signal: providedSignal || controller.signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      const stopped = providedSignal
        ? new Error('Requête annulée.')
        : new Error('La requête a expiré. Vérifiez votre connexion puis réessayez.');
      stopped.code = providedSignal ? 'REQUEST_CANCELLED' : 'REQUEST_TIMEOUT';
      throw stopped;
    }
    // Never delete the candidate's session on transport failure. The caller
    // may retry once connectivity returns, and queued progress remains intact.
    const offline = new Error('Connexion au serveur impossible. Vérifiez votre réseau puis réessayez.');
    offline.code = 'NETWORK_UNAVAILABLE';
    offline.cause = error;
    throw offline;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }

  const rawBody = await response.text();
  let data = null;

  if (rawBody) {
    try {
      data = JSON.parse(rawBody);
    } catch {
      data = { message: rawBody };
    }
  }

  if (!response.ok) {
    const error = new Error(
      data?.error || data?.message || `Erreur serveur (${response.status})`
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export { API_URL };
export default apiClient;
